import authStorage from "src/utils/authStorage";
import { useState, useEffect, useCallback, useContext } from "react";
import api from "../utils/api";
import { AuthContext } from "../providers/AuthProvider";

export const useBotConfiguration = () => {
  const { user } = useContext(AuthContext);
  const [configuration, setConfiguration] = useState({
    completenessScore: 0,
    readyToGenerate: false,
    validationStatus: null,
    isActivated: false,
    isConfigured: false,
    loading: true,
    error: null,
    botName: null,
    companyName: null,
    extractedMetadata: {},
    actualPrompt: null, // Prompt completo com todas as camadas (sistema + configurável + especial)
    configurablePrompt: null, // Apenas o prompt configurável pelo parceiro (para exibição)
    promptConfiguration: {}, // Adiciona detalhes da configuração
    partnerDetails: null // Adiciona partner details para web search config
  });
  const [retryCount, setRetryCount] = useState(0);

  const fetchConfiguration = useCallback(async () => {
    try {
      setConfiguration(prev => ({ ...prev, loading: true, error: null }));

      // Skip API calls for admin and sales users
      if (user?.userType === 'admin' || user?.userType === 'sales') {
        console.log('Skipping bot configuration for non-partner user');
        setConfiguration({
          completenessScore: 0,
          readyToGenerate: false,
          validationStatus: null,
          isActivated: false,
          isConfigured: false,
          loading: false,
          error: null,
          botName: null,
          companyName: null,
          extractedMetadata: {},
          actualPrompt: null,
          configurablePrompt: null,
          promptConfiguration: {}
        });
        return;
      }

      // Verificar se temos token de autenticação
      const token = authStorage.getItem("@sacgpt:token");
      if (!token) {
        console.warn('Sem token de autenticação, usando valores padrão');
        setConfiguration({
          completenessScore: 0,
          readyToGenerate: false,
          validationStatus: null,
          isActivated: false,
          isConfigured: false,
          loading: false,
          error: null
        });
        return;
      }
      
      // Helper to detect 403 PHONE_REQUIRED (handled by PhoneNumberModal)
      const isPhoneRequired = (err) =>
        err?.response?.status === 403 && err?.response?.data?.code === 'PHONE_REQUIRED';

      // Buscar configuração atual do bot e o prompt real
      const [startResponse, configResponse, promptResponse] = await Promise.all([
        api.post("/partners/bot_setup/start").catch(err => { if (isPhoneRequired(err)) return null; throw err; }),
        api.get("/partners/bot_setup/current_configuration").catch(() => null),
        api.get("/partners/bot_prompt_configuration/preview").catch(() => null)
      ]);

      // Se startResponse é null por PHONE_REQUIRED, usar config padrão e retornar
      if (!startResponse) {
        setConfiguration(prev => ({ ...prev, loading: false }));
        return;
      }

      if (startResponse.data) {
        const data = startResponse.data;
        const configData = configResponse?.data?.configuration || {};
        const extractedMetadata = configData.extracted_metadata || data.extracted_metadata || {};
        const promptData = promptResponse?.data?.data || {};

        console.log('[useBotConfiguration] startResponse data:', data);
        console.log('[useBotConfiguration] configData:', configData);
        console.log('[useBotConfiguration] extractedMetadata:', extractedMetadata);
        console.log('[useBotConfiguration] Bot name from metadata:', extractedMetadata.bot_name);
        console.log('[useBotConfiguration] promptData:', promptData);

        // Converter partner_details de snake_case para camelCase
        const rawPartnerDetails = data.partner_details || configData.partner_details;
        const partnerDetails = rawPartnerDetails ? {
          id: rawPartnerDetails.id,
          partnerId: rawPartnerDetails.partner_id,
          widgetEnabled: rawPartnerDetails.widget_enabled !== false,
          connectedWithGoogle: rawPartnerDetails.connected_with_google,
          allowVoiceResponse: rawPartnerDetails.allow_voice_response,
          webSearchEnabled: rawPartnerDetails.web_search_enabled,
          webSearchAllowedDomains: rawPartnerDetails.web_search_allowed_domains || [],
          webSearchReasoningEffort: rawPartnerDetails.web_search_reasoning_effort,
          webSearchUserLocation: rawPartnerDetails.web_search_user_location
        } : null;

        setConfiguration({
          completenessScore: data.completeness_score || 0,
          readyToGenerate: data.ready_to_generate || false,
          validationStatus: data.validation_status || null,
          isActivated: data.is_activated || false,
          isConfigured: (data.completeness_score || 0) >= 0.7,
          loading: false,
          error: null,
          botName: extractedMetadata.bot_name || promptData.metadata?.bot_name || null,
          companyName: extractedMetadata.company_name || promptData.metadata?.company_name || null,
          extractedMetadata: extractedMetadata,
          actualPrompt: promptData.prompt || null,  // Prompt completo
          configurablePrompt: promptData.configurable_prompt || null,  // Apenas configurável
          promptConfiguration: promptData.configuration || {},
          // Partner details para status de integrações (convertido para camelCase)
          partnerDetails: partnerDetails,
          // Force re-render on configuration update
          lastUpdated: Date.now()
        });
        setRetryCount(0); // Reset retry count on success
      }
    } catch (error) {
      console.error('Erro ao buscar configuração do bot:', error);
      
      // Usar valores padrão em caso de erro
      const defaultConfig = {
        completenessScore: 0,
        readyToGenerate: false,
        validationStatus: null,
        isActivated: false,
        isConfigured: false,
        loading: false,
        error: error.response?.status === 500
          ? 'Erro no servidor. Usando configuração padrão.'
          : 'Não foi possível carregar a configuração do bot',
        botName: null,
        companyName: null,
        extractedMetadata: {},
        actualPrompt: null,
        configurablePrompt: null,
        promptConfiguration: {}
      };
      
      setConfiguration(prev => ({
        ...prev,
        ...defaultConfig
      }));
      
      // Implementar retry com exponential backoff
      if (retryCount < 3 && error.response?.status === 500) {
        const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 10000);
        console.log(`Tentando novamente em ${retryDelay}ms...`);
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchConfiguration();
        }, retryDelay);
      }
    }
  }, [retryCount, user]);

  const refreshConfiguration = useCallback(() => {
    fetchConfiguration();
  }, [fetchConfiguration]);

  // Função para atualizar configuração com dados do WebSocket (sem fazer API call)
  const updateFromWebSocket = useCallback((wsData) => {
    console.log('[useBotConfiguration] Updating from WebSocket:', wsData);

    setConfiguration(prev => ({
      ...prev,
      // Atualizar campos específicos se presentes no payload
      botName: wsData.bot_name || wsData.extracted_metadata?.bot_name || prev.botName,
      companyName: wsData.company_name || wsData.extracted_metadata?.company_name || prev.companyName,
      extractedMetadata: wsData.extracted_metadata || prev.extractedMetadata,
      completenessScore: wsData.completeness_score !== undefined ? wsData.completeness_score : prev.completenessScore,
      readyToGenerate: wsData.ready_to_generate !== undefined ? wsData.ready_to_generate : prev.readyToGenerate,
      validationStatus: wsData.validation_status || prev.validationStatus,
      isActivated: wsData.is_activated !== undefined ? wsData.is_activated : prev.isActivated,
      isConfigured: wsData.completeness_score >= 0.7 || prev.isConfigured,
      // Atualizar prompt em tempo real
      actualPrompt: wsData.generated_prompt || wsData.actual_prompt || prev.actualPrompt,
      configurablePrompt: wsData.configurable_prompt || prev.configurablePrompt,
      promptConfiguration: wsData.prompt_configuration || prev.promptConfiguration,
      // Atualizar partner details (convertido para camelCase)
      // Preserva valores anteriores se WebSocket não enviar campos completos
      partnerDetails: wsData.partner_details != null ? {
        id: wsData.partner_details.id,
        partnerId: wsData.partner_details.partner_id ?? prev.partnerDetails?.partnerId,
        widgetEnabled: wsData.partner_details.widget_enabled ?? prev.partnerDetails?.widgetEnabled,
        connectedWithGoogle: wsData.partner_details.connected_with_google ?? prev.partnerDetails?.connectedWithGoogle,
        allowVoiceResponse: wsData.partner_details.allow_voice_response ?? prev.partnerDetails?.allowVoiceResponse,
        webSearchEnabled: wsData.partner_details.web_search_enabled ?? prev.partnerDetails?.webSearchEnabled,
        webSearchAllowedDomains: wsData.partner_details.web_search_allowed_domains ?? prev.partnerDetails?.webSearchAllowedDomains ?? [],
        webSearchReasoningEffort: wsData.partner_details.web_search_reasoning_effort ?? prev.partnerDetails?.webSearchReasoningEffort,
        webSearchUserLocation: wsData.partner_details.web_search_user_location ?? prev.partnerDetails?.webSearchUserLocation
      } : prev.partnerDetails,
      // Atualizar timestamp para forçar re-render
      lastUpdated: Date.now(),
      loading: false
    }));
  }, []);

  useEffect(() => {
    fetchConfiguration();
  }, [fetchConfiguration]);

  // Verificar se o simulador deve ser habilitado
  const isSimulatorEnabled = configuration.completenessScore >= 0.7 && configuration.readyToGenerate;

  // Calcular progresso em porcentagem
  const progressPercentage = Math.round(configuration.completenessScore * 100);

  // Obter mensagem de status
  const getStatusMessage = () => {
    const score = configuration.completenessScore;
    if (score < 0.6) {
      return "Adicione mais informações para melhorar seu bot";
    } else if (score >= 0.6 && score < 0.7) {
      return "Quase lá! Mais alguns detalhes tornarão seu bot ainda melhor";
    } else {
      return "Excelente! Seu bot está pronto para ser ativado";
    }
  };

  return {
    ...configuration,
    isSimulatorEnabled,
    progressPercentage,
    statusMessage: getStatusMessage(),
    refreshConfiguration,
    refetch: refreshConfiguration,
    updateFromWebSocket
  };
};